import React from 'react';
import { DollarSign, Save } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PhoneInput } from './PhoneInput';

interface SalesFormProps {
  onSuccess: () => void;
}

export function SalesForm({ onSuccess }: SalesFormProps) {
  const [form, setForm] = React.useState({
    referralCode: '',
    customerName: '',
    customerWhatsapp: '',
    saleValue: '',
    saleDate: new Date().toISOString().split('T')[0]
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [isValidCode, setIsValidCode] = React.useState(false);

  const validateReferralCode = async (code: string): Promise<boolean> => {
    try {
      // Reset states
      setIsValidCode(false);
      setError(null);

      // Basic validation
      if (!code || code.length !== 9) {
        return false;
      }

      console.log('Validating code:', code); // Debug log

      // Query the database
      const { data, error, count } = await supabase
        .from('referral_codes')
        .select('*', { count: 'exact' })
        .eq('code', code.trim());

      console.log('Query result:', { data, error, count }); // Debug log

      if (error) {
        console.error('Database error:', error);
        setError('Erro ao validar código de indicação');
        return false;
      }

      const isValid = Array.isArray(data) && data.length > 0;
      console.log('Is valid:', isValid); // Debug log

      setIsValidCode(isValid);
      
      if (!isValid) {
        setError('Código de indicação não encontrado');
      }

      return isValid;
    } catch (err) {
      console.error('Validation error:', err);
      setError('Erro ao validar código de indicação');
      return false;
    }
  };

  const handleReferralCodeChange = async (code: string) => {
    const upperCode = code.toUpperCase();
    console.log('Handling code change:', upperCode); // Debug log
    
    setForm(prev => ({ ...prev, referralCode: upperCode }));
    setError(null);

    if (upperCode.length === 9) {
      await validateReferralCode(upperCode);
    } else {
      setIsValidCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate referral code before submitting
    const isValid = await validateReferralCode(form.referralCode);
    if (!isValid) {
      setError('Código de indicação inválido');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create sale record
      const { error: saleError } = await supabase
        .from('sales')
        .insert({
          referral_code: form.referralCode,
          customer_name: form.customerName,
          customer_whatsapp: form.customerWhatsapp ? `+55${form.customerWhatsapp.replace(/\D/g, '')}` : null,
          sale_value: parseFloat(form.saleValue),
          sale_date: new Date(form.saleDate).toISOString(),
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (saleError) throw saleError;

      setSuccess(true);
      setForm({
        referralCode: '',
        customerName: '',
        customerWhatsapp: '',
        saleValue: '',
        saleDate: new Date().toISOString().split('T')[0]
      });
      onSuccess();
    } catch (err) {
      console.error('Error creating sale:', err);
      setError(err instanceof Error ? err.message : 'Erro ao registrar venda');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 text-green-700 rounded-lg">
          Venda registrada com sucesso!
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Código de Indicação
          </label>
          <input
            required
            type="text"
            value={form.referralCode}
            onChange={(e) => handleReferralCodeChange(e.target.value)}
            placeholder="Digite o código"
            className={`w-full px-4 py-2 rounded-lg border ${
              form.referralCode.length === 9
                ? isValidCode
                  ? 'border-green-500 bg-green-50'
                  : 'border-red-500 bg-red-50'
                : 'border-gray-300'
            } focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
            maxLength={9}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Cliente
          </label>
          <input
            required
            type="text"
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            placeholder="Digite o nome do cliente"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            WhatsApp do Cliente (opcional)
          </label>
          <PhoneInput
            value={form.customerWhatsapp}
            onChange={(value) => setForm({ ...form, customerWhatsapp: value })}
            placeholder="(11) 98765-4321"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valor da Venda
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={form.saleValue}
              onChange={(e) => setForm({ ...form, saleValue: e.target.value })}
              placeholder="0.00"
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data da Venda
          </label>
          <input
            required
            type="date"
            value={form.saleDate}
            onChange={(e) => setForm({ ...form, saleDate: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !isValidCode}
        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Save size={20} />
        {isSubmitting ? 'Registrando...' : 'Registrar Venda'}
      </button>
    </form>
  );
}